/**
 * `org` translations, for every locale.
 *
 * Its own area file, not folded into `admin` (hr.ts) or `shell` -- see
 * index.ts's note on why each area owns a disjoint set of top-level keys.
 *
 * `tabOrganization`/`tabSignIn` label two of the five tabs on
 * `pages/admin/Config.tsx`. The sign-in half keeps its strings in
 * `otp.ts` -- it moved page, not namespace, so nothing that referenced
 * `otp.*` had to change.
 *
 * `navConfig` labels the single sidebar entry; nothing about the key's
 * meaning ties it to `org` specifically.
 *
 * ⛔ English says "Config" but `mn`/`ru` keep their full words on purpose:
 * `Тохиргоо` is already short, and Russian has no idiomatic clipping of
 * `Конфигурация`.
 */
export const organization = {
  mn: {
    org: {
      navConfig: 'Тохиргоо',
      subtitle: 'Байгууллагын мэдээлэл болон салбарын хаягийг энд удирдана.',
      tabOrganization: 'Байгууллага',
      tabSignIn: 'Кодоор нэвтрэх',
      detailsTitle: 'Байгууллагын мэдээлэл',
      nameEn: 'Нэр (Англи)',
      nameMn: 'Нэр (Монгол)',
      nameRu: 'Нэр (Орос)',
      registrationNo: 'Регистрийн дугаар',
      phone: 'Утас',
      email: 'И-мэйл',
      mailReplyTo: 'Хариу хүлээн авах хаяг',
      mailReplyToHint: 'Танай байгууллагын захидлын хариу энэ хаягаар ирнэ.',
      mailReplyToRequired: 'Хариу хүлээн авах хаяг заавал шаардлагатай.',
      website: 'Вэбсайт',
      websiteHint: 'Шинэ загварын нэхэмжлэхийн хөлд хуудасны дугаартай хамт хэвлэгдэнэ.',
      bankEnglishNames: 'Банкны англи нэр',
      bankEnglishNamesHint: 'Шинэ загварын нэхэмжлэх эдгээрийг хэвлэнэ. Хоосон бол хадгалсан нэрийг хэвлэнэ.',
      bankNameEn: 'Банкны нэр (англи)',
      accountNameEn: 'Дансны нэр (англи)',
      bankEnglishNamesSaved: 'Банкны англи нэр хадгалагдлаа',
      pauseMessages: 'Бүх и-мэйл, сануулгыг түр зогсоох',
      pauseMessagesPaused: 'Бүх и-мэйл, сануулга түр зогссон',
      pauseMessagesHelpLabel: 'Юу зогсдог вэ?',
      pauseMessagesHelp:
        'Энэ сонголтыг арилгах хүртэл и-мэйл, сануулга илгээгдэхгүй бөгөөд ээлжийн амралт олгох, хугацаа хэтэрсэн чөлөөг автоматаар татгалзах, жилийн эцсийн тохируулга зэрэг автомат ажлууд ч мөн ажиллахгүй.',
      saved: 'Хадгалагдлаа',
      addressesTitle: 'Хаягууд',
      addressesHint: 'Баримт бичигт хэвлэгдэх салбарын хаягууд.',
      empty: 'Хаяг бүртгэгдээгүй байна. Эхний салбарын хаягийг нэмнэ үү.',
      addAddress: 'Хаяг нэмэх',
      editAddress: 'Хаяг засах',
      kind: 'Төрөл',
      kindHeadOffice: 'Төв оффис',
      kindBranch: 'Салбар',
      label: 'Нэршил',
      language: 'Хэл',
      address: 'Хаяг',
      primary: 'Үндсэн хаяг',
      primaryBadge: 'Үндсэн',
      deleteConfirm: '"{{label}}" хаягийг устгах уу?',
      deactivateConfirm:
        'Бүх и-мэйл, сануулгыг түр зогсоох уу? Энэ сонголтыг арилгах хүртэл юу ч илгээгдэхгүй, автомат ажлууд ч ажиллахгүй.',
      identitiesTitle: 'Баримт бичиг гаргагч байгууллагууд',
      identitiesHint:
        'Дөрвөн компанийн нэр, регистр, НӨАТ-ын дугаар, тамга. Эдгээр нь нэг байгууллагын баримтын хувилбарууд бөгөөд ажилтан, хуваарь, цалин хуваалцагдана.',
      identitiesEmpty: 'Гаргагч байгууллага бүртгэгдээгүй байна.',
      addIdentity: 'Гаргагч нэмэх',
      editIdentity: 'Гаргагч засах',
      identitySaved: 'Гаргагч байгууллага хадгалагдлаа',
      loadDetailsFailed: 'Байгууллагын мэдээллийг ачаалж чадсангүй',
      loadAddressesFailed: 'Хаягуудыг ачаалж чадсангүй',
      loadIdentitiesFailed: 'Гаргагч байгууллагуудыг ачаалж чадсангүй',
      noChanges: 'Өөрчлөлт алга',
      fieldRequired: 'Үүнийг бөглөнө үү',
      slugInvalid: 'Жижиг латин үсэг, тоо, зураасаар 2–50 тэмдэгт; үсэг эсвэл тоогоор эхэлнэ',
      identitySlug: 'Код',
      identitySlugHint: 'Зөвхөн жижиг латин үсэг, тоо, зураас. Дараа нь өөрчлөгдөхгүй.',
      vatRegistrationNo: 'НӨАТ төлөгчийн дугаар',
      vatRegistrationNoHint:
        'Хоосон байж болно — НӨАТ төлөгч бус байгууллага хэвийн үзэгдэл. ' +
        'Зөвхөн лавлагаа: баримт дээр тухайн баримтын ӨӨРИЙН НӨАТ дугаар хэвлэгдэнэ.',
      identityDefault: 'Үндсэн гаргагч',
      identityDefaultBadge: 'Үндсэн',
      identityDefaultRequired:
        'Энэ бол цорын ганц үндсэн гаргагч. Өөр компанийг нээгээд «Үндсэн гаргагч»-ийг сонговол энэ нь үндсэн гаргагч байхаа болино.',
      identityDefaultInactive:
        'Үндсэн гаргагч идэвхтэй байх ёстой. Эхлээд өөр компанийг үндсэн гаргагч болгоно уу.',
      identityLastActive:
        'Энэ бол сүүлийн идэвхтэй компани. Үүнгүйгээр баримт гаргах боломжгүй тул идэвхгүй болгох боломжгүй.',
      identityActive: 'Идэвхтэй',
      identityInactiveBadge: 'Идэвхгүй',
      identityCannotIssue: 'Баримт гаргах боломжгүй',
      identityDefectName: 'Нэр дутуу',
      identityDefectRegistrationNo: 'Регистрийн дугаар дутуу',
      identityWarningSeal: 'Тамга байхгүй',
      logo: 'Лого',
      logoNone: 'Лого байхгүй',
      logoUpload: 'Лого нэмэх',
      logoReplace: 'Лого солих',
      logoSaved: 'Лого хадгалагдлаа',
      logoHint:
        'PNG эсвэл JPEG, 2MB-аас бага. Баримт дээр хэвлэгдэнэ; үндсэн компанийн лого хажуугийн цэс болон хүсэлт дээр мөн харагдана. Солиход аль хэдийн гаргасан баримтууд өөрчлөгдөхгүй.',
      seal: 'Тамга',
      sealNone: 'Тамга байхгүй',
      sealUpload: 'Тамга нэмэх',
      sealReplace: 'Тамга солих',
      sealSaved: 'Тамга хадгалагдлаа',
      sealHint:
        'PNG эсвэл JPEG, 2MB-аас бага. Солиход хуучин тамга хадгалагдаж, аль хэдийн гаргасан баримтууд өөрчлөгдөхгүй.',
    },
  },
  en: {
    org: {
      navConfig: 'Config',
      subtitle: "Manage the organization's own details and its branch addresses.",
      tabOrganization: 'Organization',
      tabSignIn: 'Code sign-in',
      detailsTitle: 'Organization details',
      nameEn: 'Name (English)',
      nameMn: 'Name (Mongolian)',
      nameRu: 'Name (Russian)',
      registrationNo: 'Registration no.',
      phone: 'Phone',
      email: 'Email',
      mailReplyTo: 'Reply-to address',
      mailReplyToHint: "Replies to your organization's mail go to this address.",
      mailReplyToRequired: 'A reply-to address is required.',
      website: 'Website',
      websiteHint: 'Printed in the new-style invoice footer, next to the page number.',
      bankEnglishNames: 'Bank names in English',
      bankEnglishNamesHint: 'The new-style invoice prints these. Left blank, it prints the stored names.',
      bankNameEn: 'Bank name (English)',
      accountNameEn: 'Account name (English)',
      bankEnglishNamesSaved: 'English bank names saved',
      pauseMessages: 'Pause all emails and reminders',
      pauseMessagesPaused: 'All emails and reminders are paused',
      pauseMessagesHelpLabel: 'What does this pause?',
      pauseMessagesHelp:
        'Until this is unticked no email or reminder is sent, and the scheduled jobs stop too: annual-leave grants, auto-rejecting overdue leave and the year-end leave reset.',
      saved: 'Saved',
      addressesTitle: 'Addresses',
      addressesHint: 'Branch addresses that print on documents.',
      empty: 'No addresses yet. Add the first branch address.',
      addAddress: 'Add address',
      editAddress: 'Edit address',
      kind: 'Kind',
      kindHeadOffice: 'Head office',
      kindBranch: 'Branch',
      label: 'Label',
      language: 'Language',
      address: 'Address',
      primary: 'Primary address',
      primaryBadge: 'Primary',
      deleteConfirm: 'Delete "{{label}}"?',
      deactivateConfirm:
        'Pause all emails and reminders? Nothing is sent, and the scheduled background jobs stop, until this is unticked.',
      identitiesTitle: 'Issuing companies',
      identitiesHint:
        'The four company letterheads — name, registration number, VAT number and seal. These are variants of ONE organization: staff, roster and payroll are shared across all of them.',
      identitiesEmpty: 'No issuing companies yet.',
      addIdentity: 'Add issuer',
      editIdentity: 'Edit issuer',
      identitySaved: 'Issuing company saved',
      loadDetailsFailed: 'Could not load the organization details',
      loadAddressesFailed: 'Could not load the addresses',
      loadIdentitiesFailed: 'Could not load the issuing companies',
      noChanges: 'Nothing has changed yet',
      fieldRequired: 'Fill this in',
      slugInvalid: 'Use 2–50 lower-case letters, digits and dashes, starting with a letter or digit',
      identitySlug: 'Code',
      identitySlugHint: 'Lower-case letters, digits and dashes. Cannot be changed later.',
      vatRegistrationNo: 'VAT registration no.',
      vatRegistrationNoHint:
        'May be blank — a company that is not VAT-registered correctly has none. ' +
        'Reference only: a document prints its OWN VAT number, never this one.',
      identityDefault: 'Default issuer',
      identityDefaultBadge: 'Default',
      identityDefaultRequired:
        'This is the only default issuer. Open another company and tick Default issuer; this one then stops being the default.',
      identityDefaultInactive:
        'The default issuer must stay active. Make another company the default first.',
      identityLastActive:
        'This is the last active company. No document could be issued without one, so it cannot be deactivated.',
      identityActive: 'Active',
      identityInactiveBadge: 'Inactive',
      identityCannotIssue: 'Cannot issue documents',
      identityDefectName: 'Name missing',
      identityDefectRegistrationNo: 'Registration no. missing',
      identityWarningSeal: 'No seal',
      logo: 'Logo',
      logoNone: 'No logo',
      logoUpload: 'Add logo',
      logoReplace: 'Replace logo',
      logoSaved: 'Logo saved',
      logoHint:
        "PNG or JPEG, under 2MB. Prints on documents; the main company's logo also shows in the side menu and on requests. Replacing it leaves documents already issued unchanged.",
      seal: 'Seal',
      sealNone: 'No seal',
      sealUpload: 'Add seal',
      sealReplace: 'Replace seal',
      sealSaved: 'Seal saved',
      sealHint:
        'PNG or JPEG, under 2MB. Replacing keeps the old seal, so documents already issued are unchanged.',
    },
  },
  ru: {
    org: {
      navConfig: 'Конфигурация',
      subtitle: 'Управление данными организации и адресами филиалов.',
      tabOrganization: 'Организация',
      tabSignIn: 'Вход по коду',
      detailsTitle: 'Данные организации',
      nameEn: 'Название (англ.)',
      nameMn: 'Название (монг.)',
      nameRu: 'Название (рус.)',
      registrationNo: 'Регистрационный номер',
      phone: 'Телефон',
      email: 'Эл. почта',
      mailReplyTo: 'Адрес для ответов',
      mailReplyToHint: 'Ответы на письма вашей организации приходят на этот адрес.',
      mailReplyToRequired: 'Адрес для ответов обязателен.',
      website: 'Веб-сайт',
      websiteHint: 'Печатается в нижнем колонтитуле счёта нового образца рядом с номером страницы.',
      bankEnglishNames: 'Названия банка на английском',
      bankEnglishNamesHint: 'Их печатает счёт нового образца. Если пусто, печатаются сохранённые названия.',
      bankNameEn: 'Название банка (англ.)',
      accountNameEn: 'Название счёта (англ.)',
      bankEnglishNamesSaved: 'Английские названия банка сохранены',
      pauseMessages: 'Приостановить все письма и напоминания',
      pauseMessagesPaused: 'Все письма и напоминания приостановлены',
      pauseMessagesHelpLabel: 'Что приостанавливается?',
      pauseMessagesHelp:
        'Пока отметка не снята, письма и напоминания не отправляются; также не выполняются плановые задачи: начисление отпуска, автоотклонение просроченных заявок и годовой сброс отпусков.',
      saved: 'Сохранено',
      addressesTitle: 'Адреса',
      addressesHint: 'Адреса филиалов, печатаемые в документах.',
      empty: 'Адресов пока нет. Добавьте первый адрес филиала.',
      addAddress: 'Добавить адрес',
      editAddress: 'Изменить адрес',
      kind: 'Тип',
      kindHeadOffice: 'Головной офис',
      kindBranch: 'Филиал',
      label: 'Название',
      language: 'Язык',
      address: 'Адрес',
      primary: 'Основной адрес',
      primaryBadge: 'Основной',
      deleteConfirm: 'Удалить «{{label}}»?',
      deactivateConfirm:
        'Приостановить все письма и напоминания? Пока отметка не снята, ничего не отправляется и плановые задачи не выполняются.',
      identitiesTitle: 'Компании-эмитенты',
      identitiesHint:
        'Четыре фирменных бланка — название, регистрационный номер, номер НДС и печать. Это варианты ОДНОЙ организации: сотрудники, график и зарплата общие для всех.',
      identitiesEmpty: 'Компаний-эмитентов пока нет.',
      addIdentity: 'Добавить эмитента',
      editIdentity: 'Изменить эмитента',
      identitySaved: 'Компания-эмитент сохранена',
      loadDetailsFailed: 'Не удалось загрузить данные организации',
      loadAddressesFailed: 'Не удалось загрузить адреса',
      loadIdentitiesFailed: 'Не удалось загрузить компании-эмитенты',
      noChanges: 'Изменений нет',
      fieldRequired: 'Заполните это поле',
      slugInvalid: '2–50 строчных латинских букв, цифр и дефисов, начиная с буквы или цифры',
      identitySlug: 'Код',
      identitySlugHint: 'Строчные латинские буквы, цифры и дефисы. Позже не меняется.',
      vatRegistrationNo: 'Номер плательщика НДС',
      vatRegistrationNoHint:
        'Может быть пустым — компания без регистрации НДС его не имеет. ' +
        'Только для справки: в документе печатается его СОБСТВЕННЫЙ номер НДС, а не этот.',
      identityDefault: 'Эмитент по умолчанию',
      identityDefaultBadge: 'По умолчанию',
      identityDefaultRequired:
        'Это единственный эмитент по умолчанию. Откройте другую компанию и отметьте «Эмитент по умолчанию» — тогда этот перестанет им быть.',
      identityDefaultInactive:
        'Эмитент по умолчанию должен оставаться активным. Сначала назначьте эмитентом по умолчанию другую компанию.',
      identityLastActive:
        'Это последняя активная компания. Без неё нельзя выпустить документ, поэтому её нельзя деактивировать.',
      identityActive: 'Активна',
      identityInactiveBadge: 'Неактивна',
      identityCannotIssue: 'Не может выпускать документы',
      identityDefectName: 'Нет названия',
      identityDefectRegistrationNo: 'Нет регистрационного номера',
      identityWarningSeal: 'Нет печати',
      logo: 'Логотип',
      logoNone: 'Нет логотипа',
      logoUpload: 'Добавить логотип',
      logoReplace: 'Заменить логотип',
      logoSaved: 'Логотип сохранён',
      logoHint:
        'PNG или JPEG, менее 2 МБ. Печатается на документах; логотип основной компании также показывается в боковом меню и на заявках. При замене уже выпущенные документы не меняются.',
      seal: 'Печать',
      sealNone: 'Нет печати',
      sealUpload: 'Добавить печать',
      sealReplace: 'Заменить печать',
      sealSaved: 'Печать сохранена',
      sealHint:
        'PNG или JPEG, менее 2 МБ. При замене старая печать сохраняется, уже выпущенные документы не меняются.',
    },
  },
}
